import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faChevronDown, 
  faChevronRight, 
  faFolder, 
  faFolderOpen, 
  faFileAlt,
  faPlus,
  faEdit,
  faTrash,
  faToggleOn,
  faToggleOff,
  faSearch,
  faFilter,
  faArrowLeft
} from "@fortawesome/free-solid-svg-icons";
import './accounting.css';

const ChartOfAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [statistics, setStatistics] = useState({});
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [viewMode, setViewMode] = useState('tree'); // 'tree' or 'table'
  const [currentPage, setCurrentPage] = useState('list'); // 'list', 'add', 'edit'
  
  const { token } = useAuth();
  const { t, i18n } = useTranslation();

  // نموذج إضافة/تعديل الحساب
  const [formData, setFormData] = useState({
    name: '',
    name_en: '',
    parent_id: '',
    type: 'asset',
    nature: 'debit',
    code: '',
    description: '',
    notes: '',
    opening_balance: 0,
    currency: 'SAR',
    tax_code: '',
    bank_account: '',
    sort_order: 0,
    is_active: true
  });

  useEffect(() => {
    if (currentPage === 'list') {
      fetchAccounts();
    }
  }, [searchTerm, filterType, filterLevel, currentPage]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterType) params.append('type', filterType);
      if (filterLevel) params.append('level', filterLevel);

      const response = await fetch(`https://naql.nozzm.com/api/chart-of-accounts?${params}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
      });

      const result = await response.json();
      
      if (result.status) {
        setAccounts(result.data.accounts.data);
        setStatistics(result.data.statistics);
        buildTreeData(result.data.accounts.data);
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('حدث خطأ أثناء جلب البيانات');
    } finally {
      setLoading(false);
    }
  };

  const buildTreeData = (accountsData) => {
    const accountMap = {};
    const rootAccounts = [];

    // إنشاء خريطة للحسابات
    accountsData.forEach(account => {
      accountMap[account.id] = {
        ...account,
        children: [],
        isExpanded: expandedNodes.has(account.id)
      };
    });

    // بناء الشجرة
    accountsData.forEach(account => {
      if (account.parent_id) {
        if (accountMap[account.parent_id]) {
          accountMap[account.parent_id].children.push(accountMap[account.id]);
        }
      } else {
        rootAccounts.push(accountMap[account.id]);
      }
    });

    setTreeData(rootAccounts);
  };

  const toggleNode = (nodeId) => {
    const newExpandedNodes = new Set(expandedNodes);
    if (newExpandedNodes.has(nodeId)) {
      newExpandedNodes.delete(nodeId);
    } else {
      newExpandedNodes.add(nodeId);
    }
    setExpandedNodes(newExpandedNodes);
    
    // تحديث حالة التوسيع في البيانات
    setTreeData(prevData => {
      const updateNode = (nodes) => {
        return nodes.map(node => {
          if (node.id === nodeId) {
            return { ...node, isExpanded: !node.isExpanded };
          }
          if (node.children.length > 0) {
            return { ...node, children: updateNode(node.children) };
          }
          return node;
        });
      };
      return updateNode(prevData);
    });
  };

  const renderTreeNode = (node, level = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const indent = level * 20;

    return (
      <div key={node.id} className="tree-node">
        <div 
          className={`tree-node-content ${hasChildren ? 'has-children' : 'leaf'} ${node.is_active ? 'active' : 'inactive'}`}
          style={{ paddingLeft: `${indent + 10}px` }}
        >
          <div className="tree-node-header">
            {hasChildren && (
              <button 
                className="expand-button"
                onClick={() => toggleNode(node.id)}
              >
                <FontAwesomeIcon 
                  icon={isExpanded ? faChevronDown : faChevronRight} 
                  className="expand-icon"
                />
              </button>
            )}
            
            <div className="node-icon">
              {hasChildren ? (
                <FontAwesomeIcon 
                  icon={isExpanded ? faFolderOpen : faFolder} 
                  className={isExpanded ? 'folder-open' : 'folder'}
                />
              ) : (
                <FontAwesomeIcon icon={faFileAlt} className="file" />
              )}
            </div>

            <div className="node-info">
              <div className="node-code">{node.code}</div>
              <div className="node-name">{node.name}</div>
              <div className="node-type">{getTypeText(node.type)}</div>
            </div>

            <div className="node-balance">
              {node.current_balance ? `${node.current_balance.toLocaleString()} ${node.currency}` : '0.00 SAR'}
            </div>

            <div className="node-actions">
              <button 
                className="action-btn edit-btn"
                onClick={() => handleEdit(node)}
                title="تعديل"
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
              <button 
                className="action-btn toggle-btn"
                onClick={() => handleToggleStatus(node.id)}
                title={node.is_active ? 'إلغاء التفعيل' : 'تفعيل'}
              >
                <FontAwesomeIcon icon={node.is_active ? faToggleOn : faToggleOff} />
              </button>
              {!node.is_system && (
                <button 
                  className="action-btn delete-btn"
                  onClick={() => handleDelete(node.id)}
                  title="حذف"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              )}
            </div>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className="tree-children">
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = currentPage === 'edit' 
        ? `https://naql.nozzm.com/api/chart-of-accounts/${selectedAccount.id}`
        : 'https://naql.nozzm.com/api/chart-of-accounts';
      
      const method = currentPage === 'edit' ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      
      if (result.status) {
        setCurrentPage('list');
        setFormData({
          name: '',
          name_en: '',
          parent_id: '',
          type: 'asset',
          nature: 'debit',
          code: '',
          description: '',
          notes: '',
          opening_balance: 0,
          currency: 'SAR',
          tax_code: '',
          bank_account: '',
          sort_order: 0,
          is_active: true
        });
        fetchAccounts();
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('حدث خطأ أثناء حفظ البيانات');
    }
  };

  const handleEdit = (account) => {
    setSelectedAccount(account);
    setFormData({
      name: account.name || '',
      name_en: account.name_en || '',
      parent_id: account.parent_id || '',
      type: account.type || 'asset',
      nature: account.nature || 'debit',
      code: account.code || '',
      description: account.description || '',
      notes: account.notes || '',
      opening_balance: account.opening_balance || 0,
      currency: account.currency || 'SAR',
      tax_code: account.tax_code || '',
      bank_account: account.bank_account || '',
      sort_order: account.sort_order || 0,
      is_active: account.is_active !== undefined ? account.is_active : true
    });
    setCurrentPage('edit');
  };

  const handleDelete = async (id) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الحساب؟')) {
      try {
        const response = await fetch(`https://naql.nozzm.com/api/chart-of-accounts/${id}`, {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
            'lang': i18n.language,
          },
        });

        const result = await response.json();
        
        if (result.status) {
          fetchAccounts();
        } else {
          setError(result.message);
        }
      } catch (error) {
        setError('حدث خطأ أثناء حذف الحساب');
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const response = await fetch(`https://naql.nozzm.com/api/chart-of-accounts/${id}/toggle-status`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
      });

      const result = await response.json();
      
      if (result.status) {
        fetchAccounts();
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('حدث خطأ أثناء تغيير حالة الحساب');
    }
  };

  const getTypeText = (type) => {
    const types = {
      asset: 'أصول',
      liability: 'خصوم',
      equity: 'حقوق ملكية',
      revenue: 'إيرادات',
      expense: 'مصروفات'
    };
    return types[type] || type;
  };

  const getNatureText = (nature) => {
    const natures = {
      debit: 'مدين',
      credit: 'دائن'
    };
    return natures[nature] || nature;
  };

  const getStatusText = (isActive) => {
    return isActive ? 'نشط' : 'غير نشط';
  };

  const getStatusClass = (isActive) => {
    return isActive ? 'activeIcon' : 'inactive';
  };

  // Render Add/Edit Page
  const renderAddEditPage = () => {
    return (
      <div className="container tables bg-white mt-5">
        <div className="page-header">
          <button className="back-btn" onClick={() => setCurrentPage('list')}>
            <FontAwesomeIcon icon={faArrowLeft} />
            العودة للقائمة
          </button>
          <div className="page-title">
            <h3>{currentPage === 'edit' ? 'تعديل الحساب' : '+ إضافة حساب جديد'}</h3>
            <p>{currentPage === 'edit' ? 'تعديل بيانات الحساب' : 'إنشاء حساب جديد في دليل الحسابات'}</p>
          </div>
        </div>

        <div className="form-container">
          {/* Left Column - Help Panels */}
          <div className="help-panels">
            {/* Help Panel */}
            <div className="help-panel">
              <div className="panel-header">
                <FontAwesomeIcon icon={faSearch} className="info-icon" />
                <h6>مساعدة</h6>
              </div>
              <div className="panel-content">
                <div className="help-item">
                  <strong>نوع الحساب المحاسبي:</strong>
                  <ul>
                    <li><strong>مجموعة:</strong> للحسابات التي تحتوي على حسابات فرعية</li>
                    <li><strong>دفتر أستاذ:</strong> للحسابات النهائية للمعاملات</li>
                  </ul>
                </div>
                <div className="help-item">
                  <strong>نوع الحساب المحاسبي العام:</strong>
                  <ul>
                    <li><strong>الأصول:</strong> مدين طبيعي</li>
                    <li><strong>الخصوم:</strong> دائن طبيعي</li>
                    <li><strong>حقوق الملكية:</strong> دائن طبيعي</li>
                    <li><strong>الإيرادات:</strong> دائن طبيعي</li>
                    <li><strong>المصروفات:</strong> مدين طبيعي</li>
                  </ul>
                </div>
                <div className="help-item">
                  <strong>كود الحساب:</strong>
                  <p>يتم التوليد تلقائياً حسب الحساب الأب</p>
                  <p>مثال: 1001 × 100101</p>
                </div>
              </div>
            </div>

            {/* Chart of Account Types Guide */}
            <div className="guide-panel">
              <div className="panel-header">
                <FontAwesomeIcon icon={faFolder} className="info-icon" />
                <h6>دليل أنواع الحسابات</h6>
              </div>
              <div className="panel-content">
                <div className="account-type-guide">
                  <div className="guide-item asset">
                    <h6 className="guide-title asset-color">الأصول (Asset)</h6>
                    <p>الأصول المملوكة للشركة مثل النقد والمعدات والعقارات</p>
                  </div>
                  <div className="guide-item liability">
                    <h6 className="guide-title liability-color">الخصوم (Liability)</h6>
                    <p>ديون الشركة والالتزامات المالية</p>
                  </div>
                  <div className="guide-item equity">
                    <h6 className="guide-title equity-color">حقوق الملكية (Equity)</h6>
                    <p>رأس المال والأرباح المحتجزة</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="form-section">
            <form onSubmit={handleSubmit} className="account-form">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label required">
                    نوع الحساب المحاسبي
                  </label>
                  <select
                    className="form-control"
                    value={formData.type || ''}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    required
                  >
                    <option value="">اختر نوع الحساب</option>
                    <option value="group">مجموعة</option>
                    <option value="ledger">دفتر أستاذ</option>
                  </select>
                  <small className="form-note">
                    الحسابات من نوع مجموعة لا تظهر في الفواتير والسندات
                  </small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label required">
                    اسم الحساب (عربي)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="اسم الحساب باللغة العربية"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    وصف الحساب
                  </label>
                  <textarea
                    className="form-control"
                    value={formData.description || ''}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="وصف مختصر للحساب"
                    rows="3"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    كود الحساب
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.code || ''}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    placeholder="سيتم التوليد تلقائياً"
                    readOnly={!formData.code}
                  />
                  <small className="form-note">
                    اتركه فارغاً ليتم التوليد تلقائياً حسب الحساب الأب
                  </small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label required">
                    اسم الحساب (إنجليزي)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.name_en || ''}
                    onChange={(e) => setFormData({...formData, name_en: e.target.value})}
                    placeholder="Account name in English"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    الحالة
                  </label>
                  <div className="status-toggle">
                    <input
                      type="checkbox"
                      id="status-toggle"
                      checked={formData.is_active !== undefined ? formData.is_active : true}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    />
                    <label htmlFor="status-toggle" className="toggle-label">
                      {formData.is_active ? 'نشط' : 'غير نشط'}
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    الحساب الأب
                  </label>
                  <select
                    className="form-control"
                    value={formData.parent_id || ''}
                    onChange={(e) => setFormData({...formData, parent_id: e.target.value})}
                  >
                    <option value="">بدون حساب أب</option>
                    {accounts.filter(acc => acc.type === 'group').map(account => (
                      <option key={account.id} value={account.id}>
                        {account.code} - {account.name}
                      </option>
                    ))}
                  </select>
                  <small className="form-note">
                    اختياري - للحسابات الفرعية (المجموعات فقط)
                  </small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label required">
                    نوع الحساب المحاسبي العام
                  </label>
                  <select
                    className="form-control"
                    value={formData.account_type || ''}
                    onChange={(e) => setFormData({...formData, account_type: e.target.value})}
                    required
                  >
                    <option value="">اختر نوع الحساب</option>
                    <option value="asset">الأصول</option>
                    <option value="liability">الخصوم</option>
                    <option value="equity">حقوق الملكية</option>
                    <option value="revenue">الإيرادات</option>
                    <option value="expense">المصروفات</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    قيمة الرصيد الافتتاحي
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={formData.opening_balance || 0}
                    onChange={(e) => setFormData({...formData, opening_balance: parseFloat(e.target.value) || 0})}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    نوع الرصيد الافتتاحي
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.opening_balance_type || 'مدين'}
                    readOnly
                  />
                  <small className="form-note">
                    يتغير تلقائياً حسب نوع الحساب
                  </small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    نسبة الضريبة (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={formData.tax_rate || 5.00}
                    onChange={(e) => setFormData({...formData, tax_rate: parseFloat(e.target.value) || 0})}
                    placeholder="5.00"
                  />
                  <small className="form-note">
                    يتم تعبئتها تلقائياً من إعدادات الشركة
                  </small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <div className="checkbox-group">
                    <input
                      type="checkbox"
                      id="system-account"
                      checked={formData.is_system || false}
                      onChange={(e) => setFormData({...formData, is_system: e.target.checked})}
                    />
                    <label htmlFor="system-account" className="checkbox-label">
                      حساب افتراضي للنظام
                    </label>
                  </div>
                  <small className="form-note">
                    الحسابات الافتراضية لا يمكن حذفها أو تعديلها لاحقاً
                  </small>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-save">
                  حفظ الحساب
                </button>
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={() => setCurrentPage('list')}
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger mt-3">
            {error}
          </div>
        )}
      </div>
    );
  };

  // Render List Page
  const renderListPage = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>جاري تحميل شجرة الحسابات...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="container tables bg-white mt-5">
        <div className="tableTitle d-flex justify-content-between align-items-center">
          <h3>{t('accounting.chartOfAccounts.title')}</h3>
          <div className="d-flex gap-2">
            <div className="view-mode-buttons">
              <button 
                className={`view-btn ${viewMode === 'tree' ? 'active' : ''}`}
                onClick={() => setViewMode('tree')}
              >
                <FontAwesomeIcon icon={faFolder} /> عرض الشجرة
              </button>
              <button 
                className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
              >
                <FontAwesomeIcon icon={faFileAlt} /> عرض الجدول
              </button>
            </div>
            <div className="AddServicesButton">
              <button onClick={() => setCurrentPage('add')}>
                <FontAwesomeIcon icon={faPlus} />
                {t('accounting.chartOfAccounts.addAccount')}
              </button>
            </div>
          </div>
        </div>

       

        {/* Statistics Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faFolder} />
            </div>
            <div className="stat-content">
              <h5>إجمالي الحسابات</h5>
              <span className="stat-number">{statistics.total_accounts || 0}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faToggleOn} />
            </div>
            <div className="stat-content">
              <h5>الحسابات النشطة</h5>
              <span className="stat-number">{statistics.active_accounts || 0}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faToggleOff} />
            </div>
            <div className="stat-content">
              <h5>الحسابات غير النشطة</h5>
              <span className="stat-number">{statistics.inactive_accounts || 0}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faFileAlt} />
            </div>
            <div className="stat-content">
              <h5>إجمالي الأرصدة</h5>
              <span className="stat-number">{statistics.total_balance || 0}</span>
            </div>
          </div>
        </div>

        {/* Tree View */}
        {viewMode === 'tree' && (
          <div className="tree-container">
            <div className="tree-header">
              <h5>شجرة الحسابات</h5>
              <p>اضغط على السهم لتوسيع أو طي الحسابات الفرعية</p>
            </div>
            <div className="tree-content">
              {treeData.map(node => renderTreeNode(node))}
            </div>
          </div>
        )}

        {/* Table View */}
        {viewMode === 'table' && (
          <div className="table-responsive">
            <table className="table borderless">
              <thead>
                <tr>
                  <th scope="col">الرقم</th>
                  <th scope="col">رقم الحساب</th>
                  <th scope="col">اسم الحساب</th>
                  <th scope="col">النوع</th>
                  <th scope="col">الطبيعة</th>
                  <th scope="col">المستوى</th>
                  <th scope="col">الرصيد</th>
                  <th scope="col">الحالة</th>
                  <th scope="col">الإعدادات</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <tr key={account.id}>
                    <td>{account.id}</td>
                    <td>{account.code}</td>
                    <td>{account.name}</td>
                    <td>{getTypeText(account.type)}</td>
                    <td>{getNatureText(account.nature)}</td>
                    <td>{account.level}</td>
                    <td>{account.current_balance || 0}</td>
                    <td className={getStatusClass(account.is_active)}>
                      {getStatusText(account.is_active)}
                    </td>
                    <td>
                      <div className="icons">
                        <button
                          className="editbutton"
                          onClick={() => handleEdit(account)}
                        >
                          <img src="/edit-icon.png" alt="تعديل" className="fixed-icon" />
                        </button>
                        <button
                          className="editbutton"
                          onClick={() => handleToggleStatus(account.id)}
                        >
                          <img src="/toggle-icon.png" alt="تغيير الحالة" className="fixed-icon" />
                        </button>
                        <button
                          className="editbutton"
                          onClick={() => handleDelete(account.id)}
                        >
                          <img src="/delete-icon.png" alt="حذف" className="fixed-icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {error && (
          <div className="alert alert-danger mt-3">
            {error}
          </div>
        )}
      </div>
    );
  };

  // Render based on current page
  if (currentPage === 'add' || currentPage === 'edit') {
    return renderAddEditPage();
  }

  return renderListPage();
};

export default ChartOfAccounts;
